import React, { useCallback, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { createStyles, makeStyles, useTheme } from "@material-ui/core/styles";
import PeopleIcon from "@material-ui/icons/People";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import * as toolareaActions from "../../actions/toolareaActions";
import FullScreen from "../../helpers/FullScreen";
import { FormattedMessage } from "react-intl";
import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import Drawer from "@material-ui/core/Drawer";
import MeetingDrawer from "../MeetingDrawer/MeetingDrawer";
import Democratic from "../MeetingViews/Democratic";
import Filmstrip from "../MeetingViews/Filmstrip";
import MiniView from "../MeetingViews/MiniView";
import AudioPeers from "../PeerAudio/AudioPeers";
import FullScreenView from "./FullScreenView";
import VideoWindow from "../VideoWindow/VideoWindow";
import Settings from "../Settings/Settings";
import BottomBar from "../Controls/BottomBar";
import LeaveDialog from "./LeaveDialog";
import WhiteBoardDrawer from "../WhiteBoard/WhiteBoardDrawer";
import Timer from "../Common/Timer";
import { peersLengthSelector } from "../../helpers/Selectors";
import StartLiveStreaming from "./StartLiveStreaming";
import GuardianExpand from "../Management/Messaging/icons/GuardianExpand";
import { Dialog, IconButton, Tooltip } from "@material-ui/core";
import useQueryParams from "../../utils/useQueryParams";
import * as callAction from "../../actions/callActions";
import NoteCardView from "../Management/Notes/NoteCardView";
import TopLogo from "./TopLogo";
import ConferenceOfficialReport from "../ConferenceOfficialReport";
import IncomingRequestDialog from "../RemoteControl/IncomingRequestDialog";
import ActiveSessionBanner from "../RemoteControl/ActiveSessionBanner";
import { intl } from "../../intl";
import { toPng } from "html-to-image";
import Draggable from "react-draggable";

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      width: "100%",
      height: "100%",
      backgroundColor: "rgb(36, 43, 48)",
      backgroundImage: `url(${
        window.config ? window.config.background : null
      })`,
      backgroundAttachment: "fixed",
      backgroundPosition: "center",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      transition: theme.transitions.create(["width"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      "&.toolAreaOpen": {
        width: "75vw",
        [theme.breakpoints.down("sm")]: {
          width: "100vW",
        },
      },
      "&.whiteBoardOpen": {
        width: "50vw",
        [theme.breakpoints.down("sm")]: {
          width: "100vW",
        },
      },
    },
    drawerPaper: {
      width: "25vw",
      [theme.breakpoints.down("sm")]: {
        width: "40vw",
      },
      [theme.breakpoints.down("xs")]: {
        width: "87vw",
      },
    },

    whiteBoardDrawerPaper: {
      width: "50vw",
      [theme.breakpoints.down("sm")]: {
        width: "100vW",
      },
    },
    recordingNotification: {
      position: "absolute",
      top: "60px",
    },
    roomName: {
      textShadow: "0 3px 6px rgba(0, 0, 0, 0.16)",
      fontSize: 24,
      fontWeight: 600,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: 1.21,
      letterSpacing: "normal",
      textAlign: "left",
      color: "#fff",
      [theme.breakpoints.down("xs")]: {
        fontSize: 14,
      },
    },
    roomInfo: {
      textShadow: "0 3px 6px rgba(0, 0, 0, 0.16)",
      fontSize: 16,
      fontWeight: 500,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: 1.21,
      letterSpacing: "normal",
      textAlign: "left",
      color: "#9fa4b1",
      [theme.breakpoints.down("xs")]: {
        fontSize: 14,
        display: "none",
      },
    },
    roomInfoBox: {
      margin: 24,
      position: "absolute",
      zIndex: 999,
      [theme.breakpoints.down("xs")]: {
        fontSize: 14,
        margin: 5,
        display: "none",
      },
    },
    roomParticipantsCount: {
      display: "flex",
      flexDirection: "row",
      marginTop: 4,
    },
    roomInfoItem: {
      margin: 0,
      marginLeft: 4,
    },
    draggableDiv: {
      cursor: "move",
      zIndex: 99999,
      backgroundColor: "white",
      overflowY: "auto",
      width: "auto",
      maxWidth: 700,
      height: "auto",
      maxHeight: window.innerHeight,
      position: "fixed",
    },
    miniRoom: {
      position: "relative",
      borderRadius: 24,
      overflow: "hidden",
    },
    conversationMiniToggle: {
      position: "absolute",
      top: 8,
      right: 8,
      zIndex: 1000,
      [theme.breakpoints.down("xs")]: {
        right: 35,
      },
    },
  })
);

const Room = () => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const fullscreen = new FullScreen(document);
  const { token } = useQueryParams();
  const currentCall = useSelector((state) => state.call.currentCall);
  const isMini = useSelector((state) => state.call.displayMini);
  const isNewNoteOpen = useSelector(
    (state) => state.noteManagement.isNewConferenceNoteOpen
  );
  const isOfficialReportOpenGlobal = useSelector(
    (state) => state.room.isOfficialReportOpenGlobal
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const room = useSelector((state) => state.room);
  const me = useSelector((state) => state.user.me);
  const toolAreaOpen = useSelector((state) => state.toolarea.toolAreaOpen);
  const fullScreenConsumer = useSelector(
    (state) => state.room.fullScreenConsumer
  );
  const isModerator = useSelector((state) =>
    state.me.roles.some((role) =>
      state.room.permissionsFromRoles.MODERATE_ROOM.includes(role)
    )
  );
  const hasWebinarPresenterPermission = useSelector((state) =>
    state.me.roles.some((role) =>
      state.room.permissionsFromRoles.MODERATE_WEBINAR.includes(role)
    )
  );
  const webinar = useSelector((state) => state.room.webinar);
  const whiteBoardOpen = useSelector(
    (state) => state.whiteboard.whiteBoardOpen
  );
  const isOpenOfficialReport = useSelector(
    (state) => state.meetingManagement.isOpenOfficialReport
  );
  const peersLength = useSelector((state) => peersLengthSelector(state));

  const ref = useRef();

  const onDownloadButtonClick = useCallback(() => {
    if (ref.current === null) {
      return;
    }
    setIsEditMode(false);
    setIsDownloading(true);
    toPng(ref.current, {
      cacheBust: true,
      backgroundColor: "white",
      canvasWidth: 500,
      pixelRatio: 2,
    })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = `${room.name}-official-report.png`;
        link.href = dataUrl;
        link.click();
        setIsEditMode(true);
        setIsDownloading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [ref]);

  const View = {
    filmstrip: Filmstrip,
    democratic: Democratic,
  }[room.mode];

  const handleToggleFullscreen = () => {
    if (fullscreen.fullscreenElement) {
      fullscreen.exitFullscreen();
    } else {
      fullscreen.requestFullscreen(document.documentElement);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(fullscreen.fullscreenElement !== null);
    };

    if (fullscreen.fullscreenEnabled) {
      fullscreen.addEventListener("fullscreenchange", handleFullscreenChange);
    }

    return () => {
      if (fullscreen.fullscreenEnabled) {
        fullscreen.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
      }
    };
  }, [fullscreen]);
  return (
    <div
      className={
        isMini
          ? classnames(classes.root, classes.miniRoom)
          : classnames(classes.root, {
              toolAreaOpen: toolAreaOpen,
              whiteBoardOpen: whiteBoardOpen,
            })
      }
    >
      {me?.id?.length > 8 && !token && (
        <Tooltip
          title={
            isOfficialReportOpenGlobal && isOpenOfficialReport
              ? intl.formatMessage({
                  id: "conference.closeReportFirst",
                  defaultMessage:
                    "You should minimize official report first.",
                })
              : intl.formatMessage({
                  id: "call.minimizeConversation",
                  defaultMessage: "Minimize conversation",
                })
          }
        >
          <span>
            <IconButton
              className={classes.conversationMiniToggle}
              onClick={() => {
                if (!(isOfficialReportOpenGlobal && isOpenOfficialReport)) {
                  dispatch(callAction.toggleDisplayMini());
                }
              }}
            >
              <GuardianExpand style={{ fill: "#fff" }} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <FullScreenView />
      <VideoWindow />
      {isNewNoteOpen && <NoteCardView note={{}} draggable={false} />}

      <div
        className={classes.roomInfoBox}
        style={isMini ? { display: "none" } : null}
      >
        <div className={classes.roomName}>
          <span>
            {room.name} {" | "}
            <Timer start={room.startTime} />
          </span>
        </div>

        {room.startTime > 0 && (
          <div className={classes.roomInfo}>
            <span className={classes.roomParticipantsCount}>
              <FormattedMessage
                id="room.participantsCount"
                defaultMessage="{count} {icon}"
                values={{
                  count: peersLength + 1,
                  icon: (
                    <PeopleIcon
                      style={{ marginTop: "-3px", marginLeft: "4px" }}
                    />
                  ),
                }}
              />
              {webinar && (
                <p
                  className={classes.roomInfoItem}
                  style={{ color: "#f9a825" }}
                >
                  <FormattedMessage
                    id="room.makeWebinar"
                    defaultMessage="Webinar"
                  />
                </p>
              )}

              {room.recordingNotificationStatus && (
                <span
                  className={classes.roomInfoItem}
                  style={{ color: "#f3465a", marginLeft: 8 }}
                >
                  {"| "}
                  <FormattedMessage
                    id="room.recordingNotificationMessage"
                    defaultMessage="Recording"
                  />
                </span>
              )}

              {room.liveStreamingNotificationStatus && (
                <span
                  className={classes.roomInfoItem}
                  style={{ color: "#3b66f3", marginLeft: 8 }}
                >
                  {"| "}
                  <FormattedMessage
                    id="room.liveStreamingNotificationMessage"
                    defaultMessage="Streaming"
                  />
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* <TopLogo></TopLogo> */}
      {!isMini && (
        <BottomBar
          fullscreenEnabled={fullscreen.fullscreenEnabled}
          fullscreen={isFullScreen}
          onFullscreen={handleToggleFullscreen}
        />
      )}

      {!fullScreenConsumer && !isMini && <View />}

      {!fullScreenConsumer && isMini && <MiniView />}

      {room.settingsOpen && <Settings />}

      <IncomingRequestDialog />
      <ActiveSessionBanner />

      {room.startLiveStreamingDialogOpen && <StartLiveStreaming />}

      {room.leaveDialogOpen && (
        <LeaveDialog
          isModerator={isModerator}
          webinar={webinar}
          hasWebinarPermission={hasWebinarPresenterPermission}
          currentCall={currentCall}
        />
      )}
      {!isMini && isOfficialReportOpenGlobal && (
        <Draggable bounds="parent">
          <div className={classes.draggableDiv}>
            <div ref={ref}>
              <ConferenceOfficialReport
                edit={isEditMode}
                room={room}
                onDownloadButtonClick={onDownloadButtonClick}
                isDownloading={isDownloading}
              />
            </div>
          </div>
        </Draggable>
      )}

      <nav>
        <SwipeableDrawer
          variant={isXs ? "temporary" : "temporary"}
          anchor={theme.direction === "rtl" ? "left" : "right"}
          open={toolAreaOpen}
          onClose={() => dispatch(toolareaActions.toggleToolArea())}
          onOpen={() => dispatch(toolareaActions.toggleToolArea())}
          classes={{
            paper: classes.drawerPaper,
          }}
          BackdropProps={{ style: { background: "initial" } }}
        >
          <MeetingDrawer
            closeDrawer={() => {
              dispatch(toolareaActions.toggleToolArea());
            }}
          />
        </SwipeableDrawer>

        <Drawer
          variant={isXs ? "temporary" : "persistent"}
          anchor={theme.direction === "rtl" ? "left" : "right"}
          open={whiteBoardOpen}
          classes={{
            paper: classes.whiteBoardDrawerPaper,
          }}
        >
          <WhiteBoardDrawer />
        </Drawer>
      </nav>
    </div>
  );
};

export default Room;
